import { FC, Fragment, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataRoomActivityOverview, MostViewedFileCard, SpaceKind } from 'shared';
import { twMerge } from 'tailwind-merge';

import dataRoomGlobeIcon from '../../../../assets/images/data-room-activity/data-room-globe.svg';
import mostActiveVisitorIcon from '../../../../assets/images/data-room-activity/most-active-visitor.svg';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { useAnimatedNumber } from '../../../../hooks/use-animated-number.hook';
import { StringKey } from '../../../../lang/index';
import { PageRoute } from '../../../../types/page.types';
import { useViewSpaceFile } from '../../hooks/use-view-space-file.hook';
import { BrowserFileIcon } from '../../workspaces/browser/browser-file-icon';
import { visitorKeyOf } from '../utils/visitor-key.util';
import { COUNT_UP_MS } from './activity-metric-cards';
import { LinkArrow } from './link-arrow';

// The design draws the file tiles at 38px and the others at 40.
const IconTile: FC<{ children: ReactNode; className?: string; isBrand?: boolean }> = ({
  children,
  className,
  isBrand,
}) => (
  <div
    className={twMerge(
      'flex size-10 shrink-0 items-center justify-center rounded-[7.5px] border border-gray-100 bg-[linear-gradient(180deg,#FFFFFF_0%,#EAECF0_131.25%)] shadow-[inset_0px_-2.5px_5px_0px_rgba(152,162,179,0.12)]',
      isBrand &&
        'border-brand-100 bg-[linear-gradient(180deg,#FFFFFF_0%,#EAF2FF_131.25%)] shadow-[inset_0px_-2.5px_5px_0px_rgba(84,140,228,0.24)]',
      className,
    )}
  >
    {children}
  </div>
);

type MostCardProps = {
  action: { label: StringKey; onClick: () => void };
  icon: ReactNode;
  stats: ReactNode[];
  title: StringKey;
  value: null | string;
};

const MostCard: FC<MostCardProps> = ({ action, icon, stats, title, value }) => (
  <div className="flex min-w-[240px] flex-1 flex-col gap-3 rounded-lg bg-gray-50 px-1 pb-1 pt-3">
    <span className="px-4 text-label-sm font-bold uppercase tracking-[1px] text-gray-700">
      <AppFormattedMessage id={title} />
    </span>
    <div className="flex flex-1 flex-col gap-6 rounded-md bg-white p-4 shadow-sm">
      {value === null ? (
        <span className="py-3 text-sm font-[450] text-gray-400">
          <AppFormattedMessage id="NO_ACTIVITY_YET" />
        </span>
      ) : (
        <>
          <div className="flex min-w-0 items-center gap-2">
            {icon}
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-base font-semibold text-black">{value}</span>
              <div className="flex min-w-0 items-center gap-2 text-label-sm font-medium text-gray-400">
                {stats.map((stat, index) => (
                  <Fragment key={index}>
                    {index > 0 && <span className="size-1 shrink-0 rounded-full bg-[#D9D9D9]" />}
                    <span className="truncate">{stat}</span>
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
          <button
            className="group flex w-fit items-center gap-2 text-sm font-medium leading-4 text-brand-700 hover:underline"
            onClick={action.onClick}
            type="button"
          >
            <AppFormattedMessage id={action.label} />
            <LinkArrow />
          </button>
        </>
      )}
    </div>
  </div>
);

/**
 * A count inside a sentence ("72 Views") that runs up from zero with the metric cards above it.
 * The message is given the rounded running number, so its plural follows the count as it climbs.
 */
const CountUpMessage: FC<{ count: number; id: StringKey; values?: Record<string, ReactNode> }> = ({
  count,
  id,
  values,
}) => {
  const running = useAnimatedNumber(count, { duration: COUNT_UP_MS, from: 0 });

  return <AppFormattedMessage id={id} values={{ ...values, count: Math.round(running) }} />;
};

const viewsStat = (count: number): ReactNode => (
  <CountUpMessage count={count} id="ACTIVITY_VIEWS_COUNT" />
);

const downloadsStat = (count: number): ReactNode => (
  <CountUpMessage count={count} id="ACTIVITY_DOWNLOADS_COUNT" />
);

/**
 * 20.1.1 §5. Each card is one row the server already ranked by the 20.1.0 §10 tie-break — the
 * ordering is not repeated here, because four screens ranking for themselves is exactly what
 * 20.1.0 forbids.
 *
 * The visitor card carries the address and not the key, so its link derives the key the server
 * would have handed a row and opens that visitor's profile.
 */
export const MostCards: FC<{ overview: DataRoomActivityOverview }> = ({ overview }) => {
  const { mostActiveVisitor, mostDownloadedFile, mostViewedDataRoom, mostViewedFile } = overview;
  const navigate = useNavigate();
  const viewFile = useViewSpaceFile();

  const openFile = (file: MostViewedFileCard | null) => () =>
    file &&
    void viewFile({
      fileId: file.fileId,
      space: { id: file.dataRoomId, kind: SpaceKind.DATA_ROOM },
    });

  return (
    <div className="flex flex-wrap items-stretch gap-3">
      <MostCard
        action={{
          label: 'VIEW_ANALYTICS',
          onClick: () =>
            mostViewedDataRoom &&
            navigate(
              PageRoute.DATA_ROOM_ACTIVITY_ROOM.replace(
                ':dataRoomId',
                mostViewedDataRoom.dataRoomId,
              ),
            ),
        }}
        icon={
          <IconTile isBrand>
            <img alt="" className="size-6" src={dataRoomGlobeIcon} />
          </IconTile>
        }
        stats={
          mostViewedDataRoom
            ? [viewsStat(mostViewedDataRoom.views), downloadsStat(mostViewedDataRoom.downloads)]
            : []
        }
        title="MOST_VIEWED_DATA_ROOM"
        value={mostViewedDataRoom?.name ?? null}
      />
      <MostCard
        action={{ label: 'VIEW', onClick: openFile(mostViewedFile) }}
        icon={
          <IconTile className="size-[38px]">
            <BrowserFileIcon fileName={mostViewedFile?.fileName ?? ''} size={24} />
          </IconTile>
        }
        stats={
          mostViewedFile
            ? [
                <CountUpMessage
                  count={mostViewedFile.views}
                  id="ACTIVITY_VIEWS_IN_DATA_ROOM"
                  values={{ dataRoom: mostViewedFile.dataRoomName }}
                />,
              ]
            : []
        }
        title="MOST_VIEWED_FILE"
        value={mostViewedFile?.fileName ?? null}
      />
      <MostCard
        action={{
          label: 'VISITOR_HISTORY',
          // The card names the person by address; their profile is addressed by its digest. A
          // page without Web Crypto (plain HTTP) cannot take it, and opens the Visitors list.
          onClick: () =>
            mostActiveVisitor &&
            void visitorKeyOf(mostActiveVisitor.email)
              .then((visitorKey) =>
                navigate(PageRoute.DATA_ROOM_ACTIVITY_VISITOR.replace(':visitorKey', visitorKey)),
              )
              .catch(() => navigate(PageRoute.DATA_ROOM_ACTIVITY_VISITORS)),
        }}
        icon={
          <IconTile>
            <img alt="" className="size-6" src={mostActiveVisitorIcon} />
          </IconTile>
        }
        stats={
          mostActiveVisitor
            ? [viewsStat(mostActiveVisitor.views), mostActiveVisitor.organisation]
            : []
        }
        title="MOST_ACTIVE_VISITOR"
        value={mostActiveVisitor?.name ?? null}
      />
      <MostCard
        action={{ label: 'VIEW', onClick: openFile(mostDownloadedFile) }}
        icon={
          <IconTile className="size-[38px]">
            <BrowserFileIcon fileName={mostDownloadedFile?.fileName ?? ''} size={24} />
          </IconTile>
        }
        stats={
          mostDownloadedFile
            ? [viewsStat(mostDownloadedFile.views), downloadsStat(mostDownloadedFile.downloads)]
            : []
        }
        title="MOST_DOWNLOADED_FILE"
        value={mostDownloadedFile?.fileName ?? null}
      />
    </div>
  );
};
